import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ThumbDown as ThumbDownIcon, ThumbUp as ThumbUpIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { AnimatedAxis, Axis, BarSeries, Grid, XYChart } from '@visx/xychart';
import { FONT_SIZE_16_PX, FONT_WEIGHT_NORMAL } from 'popTheme';
import { selectResultsSelectedDate } from 'redux/reducers/results';
import { convertResultCategoryToResourceCategory, getColorForQuestionnaireType } from './Results';
import { getCategoryDisplayLabel } from 'components/home/ResourceCategory';

export const getSelectedResult = (resultsArray = [], selectedDate) => {
  // Find the index of the selected date.
  const indexOfSelectedResult = resultsArray
    ? resultsArray.map((result) => result.date).indexOf(selectedDate)
    : -1;
  return indexOfSelectedResult < 0 ? [] : [resultsArray.at(indexOfSelectedResult)];
};

export const TICK_VALUES = [0, 20, 40, 60, 80, 100];

export const createDataArrayForBarSeries = (results) => {
  // We are going to get something in the shape of { date: '', mood: 20, sleep: 30, etc}.
  // We want a series of [{x: mood, y: 20}, {x: sleep, y: 30}, etc] skipping the date.
  const convertedResultsArray =
    results?.length > 0
      ? Object.keys(results[0])
          .map((key) => {
            return { x: key, y: results[0][key] };
          })
          .filter((result) => result.x !== 'date') // Filter out the date property.
      : [];

  // If there are no dates, for the chart to appear, we need an entry with undefined values.
  return convertedResultsArray.length > 0
    ? convertedResultsArray
    : [{ x: undefined, y: undefined }];
};

// This draws the trends graph given the set of results and a selected date.
// The only way to be able to test the XYChart is to allow the tests to provide a width: https://github.com/airbnb/visx/issues/951#issuecomment-785305999
export const DayGraph = ({ results, width, isMobile }) => {
  const intl = useIntl();
  const theme = useTheme();
  const selectedDate = useSelector(selectResultsSelectedDate);
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    // We only want to show about 6 points in the graph.  So the chart will only contain the 6 results around the selected date.
    setSelectedResult(getSelectedResult(results, selectedDate));
  }, [results, selectedDate]);

  const yAxisFontProps = {
    fill: theme.palette.primary.main,
    fontSize: isMobile ? '0.6875em' : FONT_SIZE_16_PX,
    fontWeight: FONT_WEIGHT_NORMAL,
    width: 'auto',
  };

  const renderThumbIcon = ({ formattedValue }) => {
    const iconProps = {
      width: isMobile ? '0.5625em' : '0.8375em',
      height: isMobile ? '0.5625em' : '0.8375em',
      sx: { color: theme.palette.primary.main },
    };

    switch (formattedValue) {
      case '0':
        return <ThumbUpIcon {...iconProps} data-testid="thumb-up-icon" />;
      case '100':
        return <ThumbDownIcon {...iconProps} data-testid="thumb-down-icon" />;
      default:
        return null;
    }
  };

  // Accessors
  const xAccessor = (result) =>
    result?.x
      ? getCategoryDisplayLabel(intl, convertResultCategoryToResourceCategory(result.x))
      : undefined;
  const yAccessor = (result) => result.y;
  const colorAccessor = (result) => getColorForQuestionnaireType(result.x);
  const resultsForBarSeries = createDataArrayForBarSeries(selectedResult);

  // TODO Doesn't seem to resize well going from wide to more narrow.  Need to add a ResizeObserver?: https://airbnb.io/visx/docs/responsive */
  return (
    <XYChart
      height={480}
      width={width}
      xScale={{ type: 'band', paddingOuter: isMobile ? 0.5 : 1, paddingInner: 0.9 }}
      yScale={{ type: 'linear', domain: [0, 100] }}
      margin={{ top: 25, left: isMobile ? 25 : 50, bottom: 75, right: 25 }}
    >
      <Axis
        orientation="bottom"
        hideTicks={true}
        hideAxisLine={true}
        tickLabelProps={{
          ...yAxisFontProps,
          dy: '1.25em',
        }}
        labelOffset={100}
        rangePadding={100}
      />
      <Axis
        orientation="left"
        hideTicks={true}
        hideAxisLine={true}
        tickValues={TICK_VALUES}
        tickLabelProps={{ ...yAxisFontProps }}
      />
      <AnimatedAxis
        orientation="right"
        hideTicks={true}
        hideAxisLine={true}
        tickValues={[0, 100]}
        tickComponent={(formattedValue) => renderThumbIcon(formattedValue)}
        top={isMobile ? -10 : -12}
      />
      <Grid
        columns={false}
        stroke={theme.palette.primary.main}
        lineStyle={{ opacity: 0.2 }}
        strokeDasharray="3"
        strokeWidth={2}
        tickValues={TICK_VALUES}
      />
      <BarSeries
        dataKey={'x'}
        data={resultsForBarSeries}
        xAccessor={xAccessor}
        yAccessor={yAccessor}
        colorAccessor={colorAccessor}
        barPadding={0.98}
      />
    </XYChart>
  );
};
